import React, { useState } from 'react';
import styled from 'styled-components';
import { formatters } from '../services/api';
import BoletoModal from './BoletoModal';
import PriorOpenChargeModal from './PriorOpenChargeModal';
import { findOldestPreviousOpenCharge } from '../utils/priorOpenCharge';
import { LuReceipt, LuTriangleAlert, LuChevronRight } from 'react-icons/lu';

const PaymentsContainer = styled.div`
  margin-bottom: 30px;
`;

const DesktopOnly = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileOnly = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
`;

const PaymentsTableStyled = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${props => props.theme.colors.white};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  table-layout: fixed;
  
  @media (max-width: 768px) {
    font-size: 12px;
    table-layout: auto;
  }
  
  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const TableHeader = styled.th`
  background: #f8fafc;
  color: ${props => props.theme.colors.primary};
  font-weight: 700;
  padding: 16px 20px;
  text-align: left;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #e2e8f0;
  
  &:nth-child(1) { width: 24%; } /* Mês/Ano */
  &:nth-child(2) { width: 16%; } /* Valor */
  &:nth-child(3) { width: 20%; } /* Vencimento */
  &:nth-child(4) { width: 20%; text-align: center; } /* Status */
  &:nth-child(5) { width: 20%; text-align: center; } /* Ação */
  
  @media (max-width: 768px) {
    padding: 10px 8px;
    font-size: 12px;
    
    &:nth-child(1) { width: 24%; }
    &:nth-child(2) { width: 18%; }
    &:nth-child(3) { width: 20%; }
    &:nth-child(4) { width: 19%; text-align: center; }
    &:nth-child(5) { width: 19%; text-align: center; }
  }
  
  @media (max-width: 480px) {
    padding: 8px 5px;
    font-size: 11px;
  }
`;

const TableData = styled.td`
  padding: 15px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  
  /* Alinhamento específico para a coluna de status e ação */
  &:nth-child(4),
  &:nth-child(5) {
    text-align: center;
    padding: 15px 10px;
  }
  
  @media (max-width: 768px) {
    padding: 12px 8px;
    font-size: 12px;
    
    &:nth-child(4),
    &:nth-child(5) {
      padding: 12px 5px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 8px 5px;
    font-size: 11px;
    
    &:nth-child(4),
    &:nth-child(5) {
      padding: 8px 3px;
    }
  }
`;

const TableRow = styled.tr`
  transition: all 0.2s ease;
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};
  
  ${props => props.$clickable && `
    &:hover {
      background-color: #f8fafc;
    }
  `}
  
  &:last-child td {
    border-bottom: none;
  }
  
  /* Destacar linhas com pagamentos em atraso */
  ${props => props.$isOverdue && `
    background-color: #fef2f2;
    border-left: 3px solid #ef4444;
    
    ${props.$clickable ? `
      &:hover {
        background-color: #fee2e2;
      }
    ` : ''}
  `}
`;

const PayButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 12px;
  border-radius: 9999px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: ${props => props.theme.colors.primary};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: #dbeafe;
    border-color: #93c5fd;
    color: #1e40af;
  }

  &:focus-visible {
    outline: 2px solid #93c5fd;
    outline-offset: 2px;
  }

  svg {
    flex-shrink: 0;
  }
`;

const PaidLabel = styled.span`
  font-size: 11px;
  color: #cbd5e1;
  font-weight: 500;
`;

const MobilePayHint = styled.div`
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px 10px;
  border-radius: 9999px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.theme.colors.primary};
  align-self: flex-start;
`;

const PaymentStatus = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
  
  ${props => {
    const s = (props.status || '').toUpperCase();
    if (s === 'PAGO') return `
      background: #ecfdf5;
      color: #059669;
      border: 1px solid #a7f3d0;
    `;
    if (s === 'VENCIDO' || props.isOverdue) return `
      background: #fef2f2;
      color: #dc2626;
      border: 1px solid #fecaca;
    `;
    if (s === 'VENCE HOJE') return `
      background: #fffbeb;
      color: #d97706;
      border: 1px solid #fef3c7;
    `;
    return `
      background: #f1f5f9;
      color: #475569;
      border: 1px solid #e2e8f0;
    `;
  }}
`;

const PaymentValue = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.grayDark};
`;

const PaymentDate = styled.span`
  color: ${props => props.theme.colors.grayMedium};
`;

const InfoNote = styled.div`
  background: ${props => props.theme.colors.lightBlue};
  color: ${props => props.theme.colors.primary};
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
  text-align: left;
  border: 1px solid #bfdbfe;
  line-height: 1.45;
  
  @media (max-width: 768px) {
    font-size: 13px;
    padding: 10px 15px;
  }
`;

// Layout mobile com cards
const MobileList = styled.div`
  display: grid;
  gap: 12px;
`;

const MobileCard = styled.div`
  background: ${props => props.theme.colors.white};
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 12px;
  
  ${props => props.$isOverdue && `
    background-color: #fef2f2;
    border-left: 4px solid #ef4444;
  `}
`;

const MobileIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
  background: ${props => props.bgColor || '#f1f5f9'};
  color: ${props => props.color || '#475569'};
`;

const MobileBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MobileTitle = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.grayDark};
  font-size: 14px;
`;

const MobileInfoText = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.grayMedium};
  
  span {
    color: ${props => props.theme.colors.grayDark};
    font-weight: 600;
  }
`;

const MobileLabel = styled.span`
  font-size: 12px;
  color: ${props => props.theme.colors.grayMedium};
`;

const OverdueIcon = styled.span`
  color: #ef4444;
  font-size: 14px;
  margin-right: 4px;
  display: inline-flex;
  align-items: center;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
`;

const MonthYearCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DashboardPaymentsTable = ({ pagamentos }) => {
  const [selectedBoleto, setSelectedBoleto] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [priorCharge, setPriorCharge] = useState(null);
  const [isPriorWarningOpen, setIsPriorWarningOpen] = useState(false);

  if (!pagamentos || pagamentos.length === 0) {
    return (
      <PaymentsContainer>
        <InfoNote>
          Nenhum pagamento encontrado para os últimos 3 meses.
        </InfoNote>
      </PaymentsContainer>
    );
  }

  // Função para verificar se um pagamento está em atraso
  const isPaymentOverdue = (payment) => {
    return payment.status === 'EM ATRASO';
  };

  const isPaymentAlreadySettled = (payment) => {
    const hasPaymentDate = payment?.pagamento && payment.pagamento !== '-';
    const isStatusPaid = (payment?.status || '').toString().toLowerCase() === 'pago';
    return hasPaymentDate || isStatusPaid;
  };

  const openBoletoModal = (pagamento) => {
    setSelectedBoleto(pagamento);
    setIsModalOpen(true);
  };

  const handleRowClick = (pagamento) => {
    if (isPaymentAlreadySettled(pagamento)) return;

    const prior = findOldestPreviousOpenCharge(pagamento, pagamentos);
    if (prior) {
      setSelectedBoleto(pagamento);
      setPriorCharge(prior);
      setIsPriorWarningOpen(true);
      return;
    }

    openBoletoModal(pagamento);
  };

  const handleContinueAfterPriorWarning = () => {
    setIsPriorWarningOpen(false);
    setPriorCharge(null);
    if (selectedBoleto) {
      setIsModalOpen(true);
    }
  };

  const handleCancelPriorWarning = () => {
    setIsPriorWarningOpen(false);
    setPriorCharge(null);
    setSelectedBoleto(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBoleto(null);
  };

  const getOrderLabel = (index) => {
    switch (index) {
      case 0: return 'Mês Anterior';
      case 1: return 'Mês Atual';
      case 2: return 'Próximo Mês';
      default: return '';
    }
  };

  return (
    <PaymentsContainer>
      <InfoNote>
        Para pagar um boleto em aberto ou vencido, clique no botão <strong>Pagar</strong> na linha correspondente.
      </InfoNote>
      
      <DesktopOnly>
        <PaymentsTableStyled>
          <thead>
            <tr>
              <TableHeader>Mês/Ano</TableHeader>
              <TableHeader>Valor</TableHeader>
              <TableHeader>Vencimento</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Ação</TableHeader>
            </tr>
          </thead>
          <tbody>
            {pagamentos.map((pagamento, index) => {
              const isOverdue = isPaymentOverdue(pagamento);
              const clickable = !isPaymentAlreadySettled(pagamento);
              
              return (
                <TableRow 
                  key={pagamento.seq || index}
                  onClick={clickable ? () => handleRowClick(pagamento) : undefined}
                  aria-label={clickable ? `Pagar boleto de ${pagamento.mesAno || getOrderLabel(index)}` : getOrderLabel(index)}
                  title={clickable ? `${getOrderLabel(index)} — Clique em Pagar para ver o boleto` : getOrderLabel(index)}
                  $clickable={clickable}
                  $isOverdue={isOverdue}
                >
                  <TableData>
                    <MonthYearCell>
                      {isOverdue && (
                        <OverdueIcon title="Pagamento em atraso">⚠️</OverdueIcon>
                      )}
                      {pagamento.mesAno || 'N/A'}
                    </MonthYearCell>
                  </TableData>
                  <TableData>
                    <PaymentValue>{formatters.formatCurrency(pagamento.valor)}</PaymentValue>
                  </TableData>
                  <TableData>
                    <PaymentDate>{pagamento.vencimentoFormatado || 'N/A'}</PaymentDate>
                  </TableData>
                  <TableData>
                    <PaymentStatus status={pagamento.status} isOverdue={isOverdue}>
                      {pagamento.status || 'N/A'}
                    </PaymentStatus>
                  </TableData>
                  <TableData>
                    {clickable ? (
                      <PayButton
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(pagamento);
                        }}
                        aria-label={`Pagar boleto de ${pagamento.mesAno || 'mês selecionado'}`}
                      >
                        Pagar
                        <LuChevronRight size={14} aria-hidden />
                      </PayButton>
                    ) : (
                      <PaidLabel>—</PaidLabel>
                    )}
                  </TableData>
                </TableRow>
              );
            })}
          </tbody>
        </PaymentsTableStyled>
      </DesktopOnly>

      <MobileOnly>
        <MobileList>
          {pagamentos.map((p, index) => {
            const isOverdue = isPaymentOverdue(p);
            const clickable = !isPaymentAlreadySettled(p);
            
            // Definir cores do ícone baseado no status
            const getIconColors = () => {
              const s = (p.status || '').toUpperCase();
              if (s === 'PAGO') return { color: '#059669', bgColor: '#ecfdf5' };
              if (s === 'VENCIDO' || isOverdue) return { color: '#dc2626', bgColor: '#fef2f2' };
              if (s === 'VENCE HOJE') return { color: '#d97706', bgColor: '#fffbeb' };
              return { color: '#475569', bgColor: '#f1f5f9' };
            };
            
            const iconColors = getIconColors();

            return (
              <MobileCard
                key={p.seq || index}
                $isOverdue={isOverdue}
                onClick={clickable ? () => handleRowClick(p) : undefined}
                style={{ cursor: clickable ? 'pointer' : 'default' }}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={clickable ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRowClick(p);
                  }
                } : undefined}
                aria-label={clickable ? `Pagar boleto de ${p.mesAno || 'mês selecionado'}` : undefined}
              >
                <MobileIcon color={iconColors.color} bgColor={iconColors.bgColor}>
                  {isOverdue ? <LuTriangleAlert /> : <LuReceipt />}
                </MobileIcon>
                
                <MobileBody>
                  <MobileHeader>
                    <MobileTitle>
                      {isOverdue && (
                        <OverdueIcon title="Pagamento em atraso">
                          <LuTriangleAlert />
                        </OverdueIcon>
                      )}
                      {p.mesAno || 'N/A'}
                    </MobileTitle>
                    <PaymentStatus status={p.status} isOverdue={isOverdue} style={{ fontSize: '10px', padding: '4px 8px' }}>
                      {p.status || 'N/A'}
                    </PaymentStatus>
                  </MobileHeader>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <MobileInfoText>
                        Venc: <span>{p.vencimentoFormatado || 'N/A'}</span>
                      </MobileInfoText>
                      {p.pagamento && p.pagamento !== '-' && (
                        <MobileInfoText>
                          Pago em: <span>{p.pagamento}</span>
                        </MobileInfoText>
                      )}
                    </div>
                    <PaymentValue style={{ fontSize: '15px' }}>
                      {formatters.formatCurrency(p.valor)}
                    </PaymentValue>
                  </div>

                  {clickable && (
                    <MobilePayHint>
                      Toque para pagar
                      <LuChevronRight size={14} aria-hidden />
                    </MobilePayHint>
                  )}
                </MobileBody>
              </MobileCard>
            );
          })}
        </MobileList>
      </MobileOnly>

      <PriorOpenChargeModal
        isOpen={isPriorWarningOpen}
        priorCharge={priorCharge}
        targetCharge={selectedBoleto}
        onContinue={handleContinueAfterPriorWarning}
        onCancel={handleCancelPriorWarning}
      />

      <BoletoModal 
        boleto={selectedBoleto}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </PaymentsContainer>
  );
};

export default DashboardPaymentsTable;

